/**
 * QboSyncCenter — what QuickBooks has sent us, and what is still waiting on a
 * decision.
 *
 * Three panels:
 *   1. Sync health: webhooks vs the 15-minute safety net, last run, counts.
 *   2. Inbox: every Estimate / Invoice / PO / Payment QBO has handed over,
 *      with the order it landed on.
 *   3. Needs mapping: QBO customers, vendors and items that aren't linked to
 *      one of our records. Nothing is auto-created, so these park here until
 *      a human points them at the right hospital, supplier or catalogue item —
 *      and the moment they do, the parked documents replay themselves.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, Loader2, Radio, AlertTriangle, CheckCircle2, Link2, Search,
  Inbox, Clock, DownloadCloud, ArrowRight, Building2, Truck, Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { API } from "@/App";

const ENTITY_META = {
  Estimate: { label: "Estimate (quote)", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  Invoice: { label: "Invoice", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  PurchaseOrder: { label: "Purchase Order", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  Payment: { label: "Payment", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  BillPayment: { label: "Bill Payment", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const STATUS_META = {
  synced: { label: "Synced", cls: "bg-emerald-100 text-emerald-800" },
  needs_mapping: { label: "Needs mapping", cls: "bg-amber-100 text-amber-900" },
  deleted_in_qbo: { label: "Deleted in QBO", cls: "bg-rose-100 text-rose-800" },
  error: { label: "Error", cls: "bg-rose-100 text-rose-800" },
};

const REF_ICON = { Customer: Building2, Vendor: Truck, Item: Package };

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const when = (iso) => {
  if (!iso) return "never";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
};

// ---------------------------------------------------------------------------
function MappingRow({ row, headers, onMapped }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(row.qbo_name || "");
  const [cands, setCands] = useState([]);
  const [busy, setBusy] = useState(false);
  const Icon = REF_ICON[row.entity] || Link2;

  const search = useCallback(async (term) => {
    try {
      const r = await axios.get(
        `${API}/admin/qbo/mappings/candidates?entity=${row.entity}&q=${encodeURIComponent(term || "")}`,
        { headers });
      setCands(r.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load candidates");
    }
  }, [row.entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (open) search(q); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const map = async (local) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/qbo/mappings`, {
        entity: row.entity, qbo_id: row.qbo_id, local_id: local.id,
        local_name: local.name || local.company_name,
      }, { headers });
      const made = r.data?.orders_created || 0;
      const replayed = (r.data?.replayed || []).length;
      toast.success(`Mapped to ${r.data.mapped_to}`, {
        description: replayed
          ? `${replayed} parked document(s) replayed${made ? `, ${made} order(s) created` : ""}.`
          : "Future documents for this name will sync straight through.",
        duration: 8000,
      });
      setOpen(false);
      onMapped?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mapping failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid={`qbo-map-row-${row.qbo_id}`}>
      <div className="flex items-center justify-between gap-3 p-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Icon className="w-3.5 h-3.5 text-slate-500" />
            {row.qbo_name || `QBO ${row.entity} ${row.qbo_id}`}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            QuickBooks {row.entity} · id {row.qbo_id}
            {row.local_name ? <> · linked to <span className="font-semibold text-emerald-700">{row.local_name}</span></> : " · not linked here yet"}
            {(row.seen_on || []).length ? ` · seen on ${row.seen_on.slice(0, 2).join(", ")}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={row.local_id ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-900 border-amber-200"}>
            {row.local_id ? "Mapped" : "Pending"}
          </Badge>
          <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)}
            data-testid={`qbo-map-open-${row.qbo_id}`}>
            <Link2 className="w-3 h-3 mr-1" /> {row.local_id ? "Re-link" : "Link it"}
          </Button>
        </div>
      </div>

      {open ? (
        <div className="border-t border-slate-100 p-3 bg-slate-50 rounded-b-lg">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search(q)}
                placeholder={`Search our ${row.entity === "Customer" ? "hospitals" : row.entity === "Vendor" ? "suppliers" : "catalogue"}`}
                className="pl-8 h-8 text-sm bg-white"
                data-testid={`qbo-map-search-${row.qbo_id}`} />
            </div>
            <Button size="sm" variant="outline" onClick={() => search(q)} className="h-8">Search</Button>
          </div>
          <ul className="mt-2 max-h-56 overflow-y-auto divide-y divide-slate-200 rounded border border-slate-200 bg-white">
            {cands.length === 0 ? (
              <li className="px-3 py-2 text-xs text-slate-500">Nothing matched — try a shorter search.</li>
            ) : cands.map((c) => (
              <li key={c.id}>
                <button type="button" disabled={busy} onClick={() => map(c)}
                  className="w-full text-left px-3 py-2 hover:bg-emerald-50 disabled:opacity-50"
                  data-testid={`qbo-map-pick-${c.id}`}>
                  <p className="text-sm font-semibold text-slate-900">{c.name || c.company_name || c.id}</p>
                  <p className="text-[10px] text-slate-500">
                    {[c.legal_entity_name, c.sku, [c.city, c.state].filter(Boolean).join(", ")]
                      .filter(Boolean).join(" · ") || c.id}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
export default function QboSyncCenter({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [state, setState] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState("");
  const [tab, setTab] = useState("inbox");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, i, m] = await Promise.all([
        axios.get(`${API}/admin/qbo/state`, { headers }),
        axios.get(`${API}/admin/qbo/inbox?limit=100${q ? `&q=${encodeURIComponent(q)}` : ""}`, { headers }),
        axios.get(`${API}/admin/qbo/mappings`, { headers }),
      ]);
      setState(s.data);
      setInbox(i.data?.rows || []);
      setMaps(m.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load QuickBooks sync state");
    } finally {
      setLoading(false);
    }
  }, [token, q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const runSync = async (mode) => {
    setSyncing(mode);
    try {
      const r = await axios.post(`${API}/admin/qbo/sync-now`,
        mode === "backfill" ? { mode: "backfill", days: 90 } : { mode: "cdc", days: 7 },
        { headers });
      if (r.data?.ok === false) {
        toast.error(r.data.error || "Sync could not run");
      } else {
        const s = r.data?.summary || {};
        toast.success(mode === "backfill" ? "Import finished" : "Sync finished", {
          description: `${s.imported ?? s.changed ?? 0} document(s) read · ${s.orders_created || 0} order(s) created`
            + (s.needs_mapping ? ` · ${s.needs_mapping} waiting on a mapping` : ""),
          duration: 9000,
        });
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing("");
    }
  };

  const resync = async (row) => {
    try {
      const r = await axios.post(`${API}/admin/qbo/inbox/${row.entity}/${row.qbo_id}/resync`, {}, { headers });
      toast.success(`${row.entity} ${row.doc_number || row.qbo_id}: ${r.data?.status || "done"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-pull failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500" data-testid="qbo-sync-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading QuickBooks sync…
      </div>
    );
  }

  const pending = maps.filter((m) => !m.local_id);
  const mapped = maps.filter((m) => m.local_id);

  return (
    <div className="space-y-5" data-testid="qbo-sync-center">
      {/* Health strip */}
      <div className="grid md:grid-cols-4 gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Real-time</p>
          <p className="text-sm font-bold mt-1 flex items-center gap-1.5">
            {state?.webhooks_configured
              ? <><Radio className="w-3.5 h-3.5 text-emerald-600" /> Webhooks live</>
              : <><AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Polling only</>}
          </p>
          <p className="text-[11px] text-slate-500 mt-1">
            {state?.webhooks_configured
              ? "QuickBooks pushes changes the moment they happen."
              : "Add the Intuit verifier token to drop from 15 minutes to seconds."}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Last sync</p>
          <p className="text-sm font-bold mt-1 flex items-center gap-1.5" data-testid="qbo-last-sync">
            <Clock className="w-3.5 h-3.5 text-cyan-600" /> {when(state?.last_run_at)}
          </p>
          <p className="text-[11px] text-slate-500 mt-1">
            {state?.last_run_summary
              ? `${state.last_run_summary.changed ?? state.last_run_summary.imported ?? 0} changed · ${state.last_run_summary.orders_created || 0} order(s) created`
              : state?.last_error ? state.last_error.slice(0, 80) : "No run yet — the cron runs every 15 minutes."}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Mirrored</p>
          <p className="text-sm font-bold mt-1" data-testid="qbo-mirrored-count">
            {Object.values(state?.counts || {}).reduce((a, b) => a + b, 0)} documents
          </p>
          <p className="text-[11px] text-slate-500 mt-1">
            {Object.entries(state?.counts || {}).filter(([, v]) => v)
              .map(([k, v]) => `${v} ${k}`).join(" · ") || "Nothing imported yet."}
          </p>
        </div>
        <div className={`rounded-lg border p-3 ${pending.length ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Waiting on you</p>
          <p className="text-sm font-bold mt-1" data-testid="qbo-pending-count">
            {pending.length} to map · {state?.needs_mapping || 0} parked
          </p>
          <p className="text-[11px] text-slate-600 mt-1">
            Nothing is auto-created — unknown names wait here.
          </p>
        </div>
      </div>

      {/* Conflict rule + actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg bg-slate-900 text-slate-300 p-3">
        <p className="text-[11px]">
          <span className="font-bold text-white">House rule:</span> QuickBooks wins on money — lines,
          prices, terms and balances are overwritten from QBO on every pass. Ship-to, confirmed
          facility and tracking stay ours, because QuickBooks does not know them.
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={!!syncing}
            className="bg-transparent text-white border-slate-600 hover:bg-slate-800"
            onClick={() => runSync("cdc")} data-testid="qbo-sync-now-btn">
            {syncing === "cdc" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />} Sync now
          </Button>
          <Button size="sm" variant="outline" disabled={!!syncing}
            className="bg-transparent text-white border-slate-600 hover:bg-slate-800"
            onClick={() => runSync("backfill")} data-testid="qbo-backfill-btn">
            {syncing === "backfill" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <DownloadCloud className="w-3.5 h-3.5 mr-1.5" />} Import last 90 days
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {[["inbox", `Inbox (${inbox.length})`], ["mapping", `Needs mapping (${pending.length})`],
          ["mapped", `Mapped (${mapped.length})`]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px ${
              tab === k ? "border-emerald-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`qbo-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "inbox" ? (
        <div data-testid="qbo-inbox">
          <div className="relative mb-2 max-w-sm">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-8 text-sm"
              placeholder="Doc number, customer, vendor or order id" data-testid="qbo-inbox-search" />
          </div>
          {inbox.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-slate-200 p-8 text-center">
              <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-700">Nothing has come over from QuickBooks yet</p>
              <p className="text-xs text-slate-500 mt-1">
                {state?.connected
                  ? "Hit “Import last 90 days” to pull the back catalogue, or create an estimate in QuickBooks and watch it land."
                  : "Connect a QuickBooks company on the Connection tab first."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Document</th>
                    <th className="text-left px-3 py-2">Who</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-right px-3 py-2">Balance</th>
                    <th className="text-left px-3 py-2">On our desk</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {inbox.map((r) => {
                    const em = ENTITY_META[r.entity] || { label: r.entity, cls: "bg-slate-100 text-slate-600" };
                    const sm = STATUS_META[r.status] || { label: r.status || "—", cls: "bg-slate-100 text-slate-600" };
                    return (
                      <tr key={`${r.entity}-${r.qbo_id}`} className="hover:bg-slate-50"
                        data-testid={`qbo-inbox-row-${r.qbo_id}`}>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={`${em.cls} text-[9px] font-bold uppercase`}>{em.label}</Badge>
                          <p className="font-semibold text-slate-900 mt-1">{r.doc_number || `#${r.qbo_id}`}</p>
                          <p className="text-[10px] text-slate-500">{r.txn_date || "—"} · {r.line_count} line(s)</p>
                        </td>
                        <td className="px-3 py-2 text-slate-700">{r.party_name}</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-900">{money(r.total)}</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-600">{money(r.balance)}</td>
                        <td className="px-3 py-2">
                          {r.local_id ? (
                            <a href={`/admin/backorders?order=${r.local_id}`}
                              className="text-cyan-700 font-semibold hover:underline inline-flex items-center gap-1">
                              {r.local_id} <ArrowRight className="w-3 h-3" />
                            </a>
                          ) : <span className="text-slate-400 text-xs">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${sm.cls}`}>{sm.label}</span>
                          {r.last_error ? <p className="text-[10px] text-amber-800 mt-1 max-w-xs">{r.last_error}</p> : null}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => resync(r)}
                            title="Re-pull this document from QuickBooks"
                            data-testid={`qbo-resync-${r.qbo_id}`}>
                            <RefreshCw className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {tab === "mapping" ? (
        <div className="space-y-2" data-testid="qbo-mapping-queue">
          {pending.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-emerald-200 bg-emerald-50/40 p-8 text-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-800">Nothing waiting on a mapping</p>
              <p className="text-xs text-slate-500 mt-1">
                Every QuickBooks name that has come through is linked to one of our records.
              </p>
            </div>
          ) : pending.map((row) => (
            <MappingRow key={`${row.entity}-${row.qbo_id}`} row={row} headers={headers} onMapped={load} />
          ))}
        </div>
      ) : null}

      {tab === "mapped" ? (
        <div className="space-y-2" data-testid="qbo-mapped-list">
          {mapped.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No mappings yet.</p>
          ) : mapped.map((row) => (
            <MappingRow key={`${row.entity}-${row.qbo_id}`} row={row} headers={headers} onMapped={load} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
